import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { IngestionStatusInfo } from 'new/elements/FileItemsList/types';
import useMetricsAdapter, {
  UseMetricsAdapterReturnedType,
} from 'new/state/metrics/adapter/useMetricsAdapter';
import { Link } from '@mui/material';
import * as V1_ROUTES from 'js/constants/pathnames';
import * as V2_ROUTES from 'new/constants/pathnames';
import FormatUtils from 'js/lib/FormatUtils';
import { useMetricsRepository } from 'new/state/metrics/repository/useMetricsRepository';
import { FileItemRow } from './types';
import { tryCatchAsync } from 'new/utils/TryCatch';
import DateUtils from 'js/lib/DateUtils';

function generateColumns(resourceType = 'data_sources', options: any = {}) {
  const isSource = resourceType === 'data_sources';

  const shouldHideRecords = options.shouldHideRecords ?? false;

  return [
    {
      name: isSource ? 'Last Ingested' : 'Last Written',
      field: 'lastIngested',
      renderRow: (row: FileItemRow) => {
        const timestamp = isSource ? row.lastIngested : row.lastWritten;
        return DateUtils.getDateString(timestamp);
      },
    },
    {
      name: 'Run ID',
      field: 'run_id',
      renderRow: (row: FileItemRow) => row.runId,
    },
    {
      name: 'Size',
      field: 'size',
      renderRow: (row: FileItemRow) => FormatUtils.bytesToString(row.size),
    },
    {
      name: 'Nexset',
      field: 'nexset_id',
      renderRow: (row: FileItemRow) => {
        const dataSetID = row.dataSetId;
        const to = `${V1_ROUTES.ROUTE_REDESIGN}${V2_ROUTES.ROUTE_MY_NEXSETS}`;
        // @ts-ignore
        return <Link to={`${to}/${dataSetID}`}>{dataSetID}</Link>;
      },
    },
    ...(shouldHideRecords
      ? []
      : [
          {
            name: 'Record Count',
            field: 'record_count',
            renderRow: (row: FileItemRow) => FormatUtils.formatNumber(row.recordCount),
          },
        ]),
    {
      name: isSource ? 'Ingestion Status' : 'Write Status',
      field: 'status',
      renderRow: (row: FileItemRow) =>
        isSource ? IngestionStatusInfo[row.ingestionStatus]?.title : row.writeStatus,
    },
  ];
}

interface UseFileItemDetailsReturn {
  error: boolean;
  loading: boolean;
  data: UseMetricsAdapterReturnedType<'requestFileDetails'>['data'];
}

export type FileItemDetailsColumnsOptions = {
  shouldHideRecords?: boolean;
};

type GetColumnsReturnType = Array<{
  name: string;
  field: string;
  renderRow: (row: any) => string | number | ReactNode;
}>;

interface UseFileItemDetailsColumns {
  fetch: (sourceId: number, fileName: string) => void;
  generateColumns: (
    resourceType: string,
    options?: FileItemDetailsColumnsOptions,
  ) => GetColumnsReturnType;
}

type UseFileItemDetails = [UseFileItemDetailsReturn, UseFileItemDetailsColumns];
type Props = {
  resourceId: number;
  fileName: string;
  resourceType: string;
};

export function useFileItemDetails(props: Props): UseFileItemDetails {
  const { resourceId, fileName, resourceType } = props;
  const { getFileDetailsByFileName } = useMetricsRepository();
  const { requestFileDetails } = useMetricsAdapter();

  const details = getFileDetailsByFileName(fileName);

  const fetch = useCallback(async (resourceId: number, fileName: string): Promise<void> => {
    await tryCatchAsync(requestFileDetails(resourceId, fileName, resourceType));
  }, []);

  useEffect(() => {
    if (!details?.data) {
      (async () => {
        await fetch(resourceId, fileName);
      })();
    }
  }, [details?.data]);

  return [
    { error: details?.error, data: details?.data, loading: details?.loading },
    { fetch, generateColumns },
  ];
}
